import { useState, useEffect } from 'react';
import { useListContext } from 'react-admin';
import { TextField, InputAdornment, IconButton, useTheme } from '@mui/material';
import { Search, X } from 'lucide-react';
import { COLORS } from '../../theme/tokens';

interface SearchInputEnterOnlyProps {
  source: string;
  placeholder?: string;
}

/**
 * Custom SearchInput that only submits on Enter key press
 * Prevents auto-filtering while typing for better performance
 */
export const SearchInputEnterOnly = ({
  source,
  placeholder = 'Search',
}: SearchInputEnterOnlyProps) => {
  const { filterValues, setFilters } = useListContext();
  const theme = useTheme();
  const [localValue, setLocalValue] = useState('');

  // Initialize from URL filter values (for persistence)
  useEffect(() => {
    if (filterValues[source]) {
      setLocalValue(filterValues[source] as string);
    }
  }, [filterValues, source]);

  // Check if filter is active
  const isActive = !!filterValues[source];

  const activeColor =
    theme.palette.mode === 'dark'
      ? COLORS.activeFilterBackgroundDark
      : COLORS.activeFilterBackgroundLight;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Update the filter when Enter is pressed
      const newFilters = { ...filterValues, [source]: localValue || undefined };
      setFilters(newFilters, filterValues);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update local state only (don't trigger filter)
    setLocalValue(event.target.value);
  };

  const handleClear = () => {
    // Clear both local state and filter
    setLocalValue('');
    const newFilters = { ...filterValues };
    delete newFilters[source];
    setFilters(newFilters, filterValues);
  };

  return (
    <TextField
      placeholder={placeholder}
      value={localValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      size="small"
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          backgroundColor: isActive ? activeColor : 'transparent',
          '&:hover': {
            backgroundColor: isActive ? activeColor : 'transparent',
          },
          '&.Mui-focused': {
            backgroundColor: isActive ? activeColor : 'transparent',
          },
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search size={18} />
          </InputAdornment>
        ),
        endAdornment: localValue ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={handleClear}
              edge="end"
              sx={{ mr: -1 }}
            >
              <X size={18} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
};
